import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Star, Camera, Shield, Zap, Home, Lock, Square } from "lucide-react";
import ListingImage from "./ListingImage";
import { formatNumber, formatUsd } from "../lib/currencyDisplay";
import { PARKING_FALLBACK } from "../lib/parkingImages";

const FACILITY_ICONS = {
  camera: { icon: <Camera size={11} />, label: "Camera" },
  security_24_7: { icon: <Shield size={11} />, label: "24/7 security" },
  ev_charging: { icon: <Zap size={11} />, label: "EV charging" },
  covered_roof: { icon: <Home size={11} />, label: "Covered" },
  gated: { icon: <Lock size={11} />, label: "Gated" },
  fenced: { icon: <Square size={11} />, label: "Fenced" },
};

export default function ParkingCard({ p, index = 0 }) {
  const navigate = useNavigate();

  return (
    <Link
      to={`/parking/${p.spot_id}`}
      className="zc-card overflow-hidden block fade-up"
      style={{ animationDelay: `${0.05 * (index % 6)}s` }}
      data-testid={`parking-card-${p.spot_id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--zc-bg-3)]">
        <ListingImage images={p.images} fallback={PARKING_FALLBACK} alt={p.name} className="w-full h-full object-cover tilt-img" />
        <div className="absolute top-3 left-3 zc-badge font-semibold bg-white/95">
          {p.space_type?.replace("_", " ")}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1 text-xs font-semibold bg-[var(--zc-success)] text-white px-2 py-1 rounded-full">
          <span className="w-1.5 h-1.5 bg-white rounded-full" /> Available
        </div>
      </div>
      <div className="p-5 flex flex-col gap-3">
        <div>
          <div className="zc-title text-lg leading-tight line-clamp-1">{p.name}</div>
          <div className="flex items-center gap-1 text-xs text-[var(--zc-text-3)] mt-1">
            <MapPin size={11} /> {p.address}, {p.city}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(p.facilities || []).slice(0, 3).map(f => {
            const m = FACILITY_ICONS[f]; if (!m) return null;
            return (
              <span key={f} className="inline-flex items-center gap-1 px-2 py-1 text-2xs zc-card-soft text-[var(--zc-text-2)] rounded-full" data-testid={`pfac-${p.spot_id}-${f}`}>
                {m.icon} {m.label}
              </span>
            );
          })}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-[var(--zc-border)]">
          <div>
            <div className="zc-price text-2xl">${formatUsd(p.price_per_hour)}</div>
            <div className="zc-unit font-semibold">/hour</div>
          </div>
          <div className="flex items-center gap-3">
            {p.avg_rating > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <Star size={11} className="text-[var(--zc-accent)]" fill="currentColor" />
                <span className="font-semibold">{formatNumber(p.avg_rating, 1)}</span>
              </div>
            )}
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/parking/${p.spot_id}`); }}
              className="px-4 h-9 zc-btn-primary text-xs"
              data-testid={`book-${p.spot_id}`}
            >
              Book Space
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
